// ---------------------------------------------------------------------------
// tempo.ts — the playback tempo of the AI voice (pitch-preserving, see
// timeStretch.ts). The sheet's Pace picks a default; the overlay's −/+ tunes
// it during a call and the tuned value is remembered per pace in this browser.
// ---------------------------------------------------------------------------

export type VoicePace = "natural" | "brisk" | "fast";

/** Default playback rate per pace — brisk is the Kozhikode tempo. */
export const PACE_TEMPO: Record<VoicePace, number> = { natural: 1, brisk: 1.15, fast: 1.3 };
export const TEMPO_MIN = 1;
export const TEMPO_MAX = 1.6;
export const TEMPO_STEP = 0.05;

const key = (pace: VoicePace) => `xale_ai_telecaller_tempo_${pace}`;

export function clampTempo(rate: number): number {
  const r = Number.isFinite(rate) ? rate : 1;
  return Math.round(Math.min(TEMPO_MAX, Math.max(TEMPO_MIN, r)) * 100) / 100;
}

export function readTunedTempo(pace: VoicePace | undefined): number {
  const p: VoicePace = pace ?? "brisk";
  try {
    const v = Number(localStorage.getItem(key(p)));
    if (v >= TEMPO_MIN && v <= TEMPO_MAX) return clampTempo(v);
  } catch {
    /* private mode */
  }
  return PACE_TEMPO[p];
}

export function writeTunedTempo(pace: VoicePace | undefined, rate: number): void {
  try {
    localStorage.setItem(key(pace ?? "brisk"), String(clampTempo(rate)));
  } catch {
    /* private mode */
  }
}

export function formatTempo(rate: number): string {
  return `${(Math.round(rate * 100) / 100).toFixed(2).replace(/0$/, "")}×`;
}
